import * as React from "react";
import {
  ImageField,
  TextInput,
  ImageInput,
  BooleanInput,
  Edit,
  FormDataConsumer,
  NumberInput,
  SelectInput,
  ReferenceInput,
  ReferenceArrayInput,
  CheckboxGroupInput,
  DateTimeInput,
  AutocompleteInput,
  AutocompleteArrayInput,
  useRecordContext,
  Title,
  ArrayInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
  SelectArrayInput,
  TimeInput,
} from "react-admin";
import { SimpleForm } from "../common/SimpleForm";
import { IMAGE_MAX_SIZE } from "../constants/FileConstants";
import {
  BANNER_SCHEDULE_TYPES,
  BANNER_SCHEDULE_TYPES_CHOICES,
} from "../enums/BannerScheduleTypes";
import { BANNER_TYPES } from "../enums/BannerTypes";
import { validateOrderOptional, validateRequired } from "../utils/Validations";
import { RESTAURANT_TYPES } from "../enums/RestaurantTypes";
import { Box, Grid } from "@mui/material";
import Spacer from "../common/Spacer";
import { RichTextInput } from "ra-input-rich-text";
import RichTextInputToolbar from "../common/RichTextInputToolbar";
import { getCountries } from "../enums/Countries";
import { ENTITY_AVAILABILITY_STATUS_OPTIONS } from "../utils/EntityAvailabilityStatusUtils";
import { ENTITY_AVAILABILITY_STATUS } from "../enums/EntityAvailabilityStatus";
import { DAYS } from "../constants/Days";
import { formatTimeOnCreate, parseTimeTimezone } from "../utils/TimeInputUtils";
import { DAYS_OPTIONS } from "../utils/DateTimeUtils";
import { useWatch } from "react-hook-form";
import AbsoluteSource from "../common/AbsoluteSource";

const BannerTitle = () => {
  const record = useRecordContext();
  return <Title title={`Banner "${record?.title}"`} />;
};

const BannerProductRow = () => {
  const { index } = useSimpleFormIteratorItem();
  const restaurantProduct = useWatch({ name: "restaurantProduct" });
  return (
    <Box sx={{ flex: "1" }}>
      <Box sx={{ display: "flex" }}>
        <AbsoluteSource>
          <ReferenceInput
            source={`restaurantProduct[${index}]`}
            reference="restaurants"
            perPage={100}
            filter={{
              active: true,
              type: RESTAURANT_TYPES.REGULAR,
            }}
            fullWidth
          >
            <AutocompleteInput
              filterToQuery={(searchText) => ({
                name: searchText,
              })}
              label="Restaurante"
              optionText="name"
              fullWidth
            />
          </ReferenceInput>
        </AbsoluteSource>

        <Spacer />

        <ReferenceInput
          // Empty source = the array's scalar element (products.<i>)
          source=""
          reference="products"
          perPage={10000}
          sort={{ field: "name", order: "ASC" }}
          filter={{
            restaurant: restaurantProduct?.[index],
            status: [ENTITY_AVAILABILITY_STATUS.ACTIVE],
          }}
          fullWidth
        >
          <SelectInput
            optionText="name"
            label="Producto"
            readOnly={!restaurantProduct?.[index]}
            fullWidth
          />
        </ReferenceInput>
      </Box>
    </Box>
  );
};

const RecurrentScheduleTimeRow = () => {
  // Relative source: the SimpleFormIterator's SourceContext already resolves
  // to recurrentSchedule.<i>.times.<j>
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <TimeInput
        source="from"
        label="Desde"
        parse={parseTimeTimezone(null, true)}
        format={formatTimeOnCreate}
        defaultValue="00:00"
        variant="outlined"
        margin="dense"
        validate={validateRequired}
        style={{ width: 150 }}
      />
      <TimeInput
        source="to"
        label="Hasta"
        parse={parseTimeTimezone(null, true)}
        format={formatTimeOnCreate}
        defaultValue="23:59"
        variant="outlined"
        margin="dense"
        validate={validateRequired}
        style={{ width: 150 }}
      />
    </Box>
  );
};

const BannerEdit = () => (
  <Edit title={<BannerTitle />} mutationMode="pessimistic" redirect="list">
    <SimpleForm defaultValues={{ oneTimeAvailableDays: Object.values(DAYS) }}>
      <FormDataConsumer>
        {({ formData, ...rest }) => (
          <>
            <TextInput
              source="title"
              label="Título"
              validate={validateRequired}
              fullWidth
            />

            <ImageInput
              source="image"
              label="Imagen"
              accept={{
                "image/*": [],
              }}
              validate={validateRequired}
              maxSize={IMAGE_MAX_SIZE}
              {...(formData.featured && {
                helperText:
                  "El tamaño recomendado es de 402x314 px (exportar en 2x png/webp).",
              })}
            >
              <ImageField source="src" title="Imagen" />
            </ImageInput>
            {!formData?.image?.rawFile && (
              <ImageField source="image" {...rest} />
            )}

            <SelectInput
              label="Tipo"
              source="type"
              choices={[
                { id: BANNER_TYPES.NEWS, name: "Novedad" },
                { id: BANNER_TYPES.PRODUCT, name: "Producto(s)" },
                { id: BANNER_TYPES.RESTAURANT, name: "Restaurante(s)" },
                { id: BANNER_TYPES.VIDEO, name: "Video(s)" },
              ]}
              validate={validateRequired}
              fullWidth
            />
            {formData?.type === BANNER_TYPES.PRODUCT && (
              <ArrayInput source="products" label="">
                <SimpleFormIterator disableReordering>
                  <BannerProductRow />
                </SimpleFormIterator>
              </ArrayInput>
            )}
            {formData?.type === BANNER_TYPES.RESTAURANT && (
              <>
                <Box sx={{ flex: 1, display: "flex" }}>
                  <Box sx={{ flex: 0.5, mr: "1em" }}>
                    <SelectInput
                      label="Regular / Guía"
                      source="restaurantType"
                      choices={[
                        { id: RESTAURANT_TYPES.REGULAR, name: "Regular" },
                        { id: RESTAURANT_TYPES.GUIDE, name: "Guía" },
                      ]}
                      defaultValue={RESTAURANT_TYPES.REGULAR}
                      emptyValue={RESTAURANT_TYPES.REGULAR}
                    />
                  </Box>
                  <Box sx={{ flex: 0.5 }}>
                    <ReferenceInput
                      source="region"
                      reference="regions"
                      perPage={100}
                      sort={{ field: "name", order: "ASC" }}
                      label="Región"
                    >
                      <SelectInput optionText="name" label="Región" />
                    </ReferenceInput>
                  </Box>
                </Box>
                <ReferenceArrayInput
                  source="restaurants"
                  reference="restaurants"
                  perPage={100}
                  filter={{
                    active: true,
                    type:
                      formData?.restaurantType === RESTAURANT_TYPES.REGULAR
                        ? RESTAURANT_TYPES.REGULAR
                        : RESTAURANT_TYPES.GUIDE,
                    ...(formData?.region && { region: [formData.region] }),
                  }}
                  sort={{ field: "name", order: "ASC" }}
                >
                  <AutocompleteArrayInput
                    filterToQuery={(searchText) => ({ name: searchText })}
                    label="Restaurante(s)"
                    helperText="Puedes elegir más de un restaurante"
                    fullWidth
                    optionText="name"
                  />
                </ReferenceArrayInput>
              </>
            )}

            {formData?.type === BANNER_TYPES.RESTAURANT && (
              <RichTextInput
                toolbar={<RichTextInputToolbar />}
                source="description"
                label="Descripción"
              />
            )}

            {formData?.restaurants?.length >= 1 && (
              <>
                <ImageInput
                  source="eventImage"
                  label="Imagen de Evento"
                  accept={{
                    "image/*": [],
                  }}
                  maxSize={IMAGE_MAX_SIZE}
                >
                  <ImageField source="src" title="Imagen de Evento" />
                </ImageInput>
                {!formData?.eventImage?.rawFile && (
                  <ImageField source="eventImage" {...rest} />
                )}
              </>
            )}

            <SelectInput
              label="Tipo de Recurrencia"
              source="scheduleType"
              choices={BANNER_SCHEDULE_TYPES_CHOICES}
              validate={validateRequired}
              fullWidth
            />
            {formData?.scheduleType === BANNER_SCHEDULE_TYPES.ONE_TIME && (
              <Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimeInput
                      source="startDate"
                      label="Fecha Inicio"
                      parse={(v) => (v ? new Date(v).toISOString() : null)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DateTimeInput
                      source="endDate"
                      label="Fecha Fin"
                      parse={(v) => (v ? new Date(v).toISOString() : null)}
                      fullWidth
                    />
                  </Grid>
                  <Grid size={12}>
                    <CheckboxGroupInput
                      source="oneTimeAvailableDays"
                      label="Días disponibles"
                      row
                      choices={DAYS_OPTIONS}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
            {formData?.scheduleType === BANNER_SCHEDULE_TYPES.RECURRENT && (
              <ArrayInput
                source="recurrentSchedule"
                label="Días y Horarios"
                validate={validateRequired}
              >
                <SimpleFormIterator
                  disableReordering
                  disableAdd={formData.recurrentSchedule?.length >= 7}
                >
                  <FormDataConsumer>
                    {() => {
                      return (
                        <>
                          <CheckboxGroupInput
                            source={"days"}
                            label=""
                            choices={DAYS_OPTIONS}
                            validate={validateRequired}
                          />
                          <ArrayInput
                            source={"times"}
                            label=""
                            defaultValue={[
                              {
                                from: "00:00",
                                to: "23:59",
                              },
                            ]}
                          >
                            <SimpleFormIterator disableReordering>
                              <RecurrentScheduleTimeRow />
                            </SimpleFormIterator>
                          </ArrayInput>
                        </>
                      );
                    }}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            )}

            {formData?.type === BANNER_TYPES.NEWS && (
              <SelectArrayInput
                label="Países"
                source="countries"
                choices={getCountries()}
                validate={validateRequired}
              />
            )}

            {formData?.type === BANNER_TYPES.VIDEO && (
              <ReferenceArrayInput
                source="videos"
                reference="videos"
                perPage={100}
                filter={{
                  active: true,
                  isAvailable: true,
                }}
                sort={{ field: "name", order: "ASC" }}
              >
                <AutocompleteArrayInput
                  filterToQuery={(searchText) => ({ name: searchText })}
                  label="Video(s)"
                  helperText="Puedes elegir más de un video"
                  fullWidth
                  optionText="name"
                />
              </ReferenceArrayInput>
            )}

            <NumberInput
              source="order"
              label="Orden"
              min={1}
              validate={validateOrderOptional}
              onWheel={(e) => e.target.blur()}
              fullWidth
            />
            <BooleanInput
              source="visible"
              label="Visible"
              defaultValue={true}
              fullWidth
            />
            <BooleanInput source="featured" label="Destacado" fullWidth />
            <SelectInput
              label="Estado"
              source="status"
              choices={ENTITY_AVAILABILITY_STATUS_OPTIONS}
              validate={validateRequired}
              fullWidth
            />
          </>
        )}
      </FormDataConsumer>
    </SimpleForm>
  </Edit>
);

export default BannerEdit;
